import type { MapDirection } from '../types'

export type DirectionVisualRole = 'outbound' | 'inbound' | 'shared' | 'other'

export interface DirectionRouteGroup {
  key: string
  directions: MapDirection[]
  geometry: MapDirection['geometry']
  role: DirectionVisualRole
}

interface Coordinate {
  latitude: number
  longitude: number
}

const EARTH_RADIUS_METERS = 6_371_000
const SAMPLE_COUNT = 32
const MAX_LENGTH_RATIO = 1.12
const MAX_AVERAGE_DISTANCE_METERS = 20
const MAX_SAMPLE_DISTANCE_METERS = 55

function distanceMeters(first: Coordinate, second: Coordinate) {
  const firstLatitude = first.latitude * Math.PI / 180
  const secondLatitude = second.latitude * Math.PI / 180
  const latitudeDelta = (second.latitude - first.latitude) * Math.PI / 180
  const longitudeDelta = (second.longitude - first.longitude) * Math.PI / 180

  const x = longitudeDelta * Math.cos((firstLatitude + secondLatitude) / 2)
  const y = latitudeDelta

  return Math.hypot(x, y) * EARTH_RADIUS_METERS
}

function cumulativeDistances(path: Coordinate[]) {
  const cumulative = [0]

  for (let index = 1; index < path.length; index += 1) {
    cumulative.push(
      cumulative[index - 1] + distanceMeters(path[index - 1], path[index]),
    )
  }

  return cumulative
}

function interpolate(
  first: Coordinate,
  second: Coordinate,
  ratio: number,
): Coordinate {
  return {
    latitude: first.latitude + (second.latitude - first.latitude) * ratio,
    longitude: first.longitude + (second.longitude - first.longitude) * ratio,
  }
}

function samplePath(path: Coordinate[], count = SAMPLE_COUNT) {
  if (!path.length) return []
  if (path.length === 1 || count <= 1) return [path[0]]

  const cumulative = cumulativeDistances(path)
  const total = cumulative[cumulative.length - 1]

  if (total === 0) return Array.from({ length: count }, () => path[0])

  const result: Coordinate[] = []
  let segmentIndex = 1

  for (let sampleIndex = 0; sampleIndex < count; sampleIndex += 1) {
    const target = total * sampleIndex / (count - 1)

    while (
      segmentIndex < cumulative.length - 1
      && cumulative[segmentIndex] < target
    ) {
      segmentIndex += 1
    }

    const previousDistance = cumulative[segmentIndex - 1]
    const nextDistance = cumulative[segmentIndex]
    const segmentDistance = nextDistance - previousDistance
    const ratio = segmentDistance === 0
      ? 0
      : (target - previousDistance) / segmentDistance

    result.push(interpolate(
      path[segmentIndex - 1],
      path[segmentIndex],
      ratio,
    ))
  }

  return result
}

function pathLength(path: Coordinate[]) {
  const cumulative = cumulativeDistances(path)
  return cumulative.at(-1) ?? 0
}

function comparisonScore(
  firstSamples: Coordinate[],
  secondSamples: Coordinate[],
) {
  let total = 0
  let maximum = 0

  for (let index = 0; index < firstSamples.length; index += 1) {
    const distance = distanceMeters(firstSamples[index], secondSamples[index])
    total += distance
    maximum = Math.max(maximum, distance)
  }

  return {
    average: total / firstSamples.length,
    maximum,
  }
}

export function areDirectionGeometriesEquivalent(
  first: Coordinate[],
  second: Coordinate[],
) {
  if (!first.length || !second.length) return false

  const firstLength = pathLength(first)
  const secondLength = pathLength(second)
  const longer = Math.max(firstLength, secondLength)
  const shorter = Math.min(firstLength, secondLength)

  if (longer > 0 && shorter / longer < 1 / MAX_LENGTH_RATIO) return false

  const firstSamples = samplePath(first)
  const secondSamples = samplePath(second)

  if (firstSamples.length !== secondSamples.length) return false

  const forward = comparisonScore(firstSamples, secondSamples)
  const reverse = comparisonScore(
    firstSamples,
    [...secondSamples].reverse(),
  )
  const score = forward.average <= reverse.average ? forward : reverse

  return score.average <= MAX_AVERAGE_DISTANCE_METERS
    && score.maximum <= MAX_SAMPLE_DISTANCE_METERS
}

export function directionVisualRole(
  direction: MapDirection,
  index: number,
): Exclude<DirectionVisualRole, 'shared'> {
  const code = direction.code.trim().toUpperCase()

  if (
    code.includes('OUTBOUND')
    || code === 'OUT'
    || code.endsWith('-OUT')
  ) {
    return 'outbound'
  }

  if (
    code.includes('INBOUND')
    || code.includes('RETURN')
    || code === 'IN'
    || code.endsWith('-IN')
  ) {
    return 'inbound'
  }

  if (index === 0) return 'outbound'
  if (index === 1) return 'inbound'

  return 'other'
}

export function groupLineDirections(directions: MapDirection[]): DirectionRouteGroup[] {
  const groups: DirectionRouteGroup[] = []

  directions.forEach((direction, index) => {
    const matching = direction.geometry.length
      ? groups.find((group) => (
          group.geometry.length > 0
          && areDirectionGeometriesEquivalent(group.geometry, direction.geometry)
        ))
      : undefined

    if (matching) {
      matching.directions.push(direction)
      matching.role = 'shared'
      return
    }

    groups.push({
      key: direction.id,
      directions: [direction],
      geometry: direction.geometry,
      role: directionVisualRole(direction, index),
    })
  })

  return groups
}
